/**
 * "Cash & margin flow" card (Make Today real phase 2, 2026-09-01 —
 * docs/plans/2026-09-01-make-today-real-phase2.md). Signed weekly bars of
 * settlement-grade margin (netProceedsCents − cogsCents) for the 12 UTC
 * weeks GET /api/dashboard's `marginFlow` carries, newest last, zero
 * baseline. Polarity encoding only — positive bars use the app's positive
 * token, negative bars the negative token (same convention
 * PriceCalibrationRow/OrdersPage use for signed money), no categorical
 * palette or chart library.
 *
 * Three states, same nullable-section idiom as unitFlowDisplay/latestSettlementDisplay
 * in lib/dashboard.ts: `marginFlow === undefined` is the dashboard query
 * still loading (skeleton); `null` means the Amazon module is off or an
 * older API build hasn't shipped the field yet (module-off copy); a
 * present-but-all-zero 12 weeks means the module is on but no settlement
 * lines have posted yet (honest empty state, not a skeleton).
 */
import type { CSSProperties } from 'react';
import { money, signedMoney } from '../../lib/price-calibration';
import { Skeleton } from '../../components/Skeleton';
import type { MarginFlowWeek } from '@sourcito/schemas';

const CHART_HEIGHT_PX = 96; // matches the h-24 utility used for the container
const HALF_HEIGHT_PX = CHART_HEIGHT_PX / 2;
const MIN_BAR_PX = 2;

function weekLabel(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function isAllZero(weeks: MarginFlowWeek[]): boolean {
  return weeks.every((w) => w.netProceedsCents === 0 && w.cogsCents === 0 && w.marginCents === 0);
}

export interface MarginFlowCardProps {
  /** undefined = the dashboard query hasn't resolved yet; null = the
   *  Amazon module is off (or an older API build hasn't shipped this
   *  field). Present weeks are always exactly 12, zero-filled for weeks
   *  with no settlement activity. */
  marginFlow: { weeks: MarginFlowWeek[] } | null | undefined;
}

export function MarginFlowCard({ marginFlow }: MarginFlowCardProps) {
  // Do NOT collapse this via `marginFlow?.weeks` — optional chaining turns
  // `null` into `undefined` too, merging the module-off state into the
  // loading state. The two must stay distinguishable.
  const weeks =
    marginFlow === undefined ? undefined : marginFlow === null ? null : marginFlow.weeks;

  return (
    <div className="bg-surface border border-border-subtle rounded-xl p-5">
      <h3 className="text-sm font-medium text-fg-primary">Cash &amp; margin flow</h3>

      {weeks === undefined && (
        <>
          <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
            True margin per unit, from lot costs.
          </p>
          <Skeleton className="mt-5 h-24 rounded-md" />
        </>
      )}

      {weeks === null && (
        <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
          Connect the Amazon module to see margin flow.
        </p>
      )}

      {weeks != null && isAllZero(weeks) && (
        <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">No settlement data yet.</p>
      )}

      {weeks != null && !isAllZero(weeks) && <MarginFlowChart weeks={weeks} />}
    </div>
  );
}

// R2 item 3 (D4, docs/plans/2026-09-03-graded-kernel.md): estimate-grade
// weeks keep the SAME hue (positive/negative) but render with a diagonal
// hatch texture instead of a solid fill — a texture, not a new color, per
// the design doc's "one hue for the series" rule. CSS-only (no chart
// library): a repeating-linear-gradient overlay at reduced opacity on top
// of the solid bar.
const HATCH_STYLE: CSSProperties = {
  backgroundImage:
    'repeating-linear-gradient(45deg, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 2px, transparent 2px, transparent 5px)',
};

function MarginFlowChart({ weeks }: { weeks: MarginFlowWeek[] }) {
  const totalMargin = weeks.reduce((sum, w) => sum + w.marginCents, 0);
  const totalNetProceeds = weeks.reduce((sum, w) => sum + w.netProceedsCents, 0);
  const maxAbsMargin = Math.max(1, ...weeks.map((w) => Math.abs(w.marginCents)));
  const hasEstimateWeek = weeks.some((w) => w.marginGrade !== 'settlement');

  return (
    <>
      <p className="text-xs text-fg-tertiary mt-1 leading-relaxed">
        {signedMoney(totalMargin)} margin on {money(totalNetProceeds)} net proceeds, last 12 weeks
      </p>

      <div className="mt-5 flex items-stretch gap-[2px] h-24 relative">
        <div
          className="absolute left-0 right-0 top-1/2 border-t border-border-subtle"
          aria-hidden="true"
        />
        {weeks.map((w) => {
          const isPositive = w.marginCents >= 0;
          const barPx =
            w.marginCents === 0
              ? 0
              : Math.max(
                  MIN_BAR_PX,
                  Math.round((Math.abs(w.marginCents) / maxAbsMargin) * HALF_HEIGHT_PX)
                );
          const partial = w.cogsCoveredUnits < w.totalUnits;
          const isEstimate = w.marginGrade !== 'settlement';
          return (
            <div key={w.weekStart} className="group relative flex-1 h-24">
              <div
                className={`absolute left-1/2 -translate-x-1/2 w-1.5 ${
                  isPositive
                    ? 'bottom-1/2 bg-positive rounded-t-[4px]'
                    : 'top-1/2 bg-negative rounded-b-[4px]'
                } ${isEstimate ? 'opacity-70' : ''}`}
                style={{ height: `${barPx}px`, ...(isEstimate ? HATCH_STYLE : {}) }}
                aria-hidden="true"
              />
              <div
                className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-10 w-max max-w-[200px] rounded-md bg-surface-elev border border-border-subtle px-2.5 py-2 text-[11px] leading-relaxed text-fg-primary shadow-lg"
                role="tooltip"
              >
                <p className="font-medium">{weekLabel(w.weekStart)}</p>
                <p className="text-fg-tertiary">Net proceeds {money(w.netProceedsCents)}</p>
                <p className="text-fg-tertiary">COGS {money(w.cogsCents)}</p>
                <p>Margin {signedMoney(w.marginCents)}</p>
                <p className="text-fg-tertiary">
                  Grade: {w.marginGrade === 'settlement' ? 'Settlement' : 'Estimate'}
                </p>
                {partial && (
                  <p className="text-fg-tertiary">
                    COGS covers {w.cogsCoveredUnits} of {w.totalUnits} units
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-[11px] text-fg-tertiary mt-2 leading-relaxed">
        Settlements post ~2 weeks behind — the newest weeks can show low or zero proceeds.
      </p>
      {hasEstimateWeek && (
        <p className="text-[11px] text-fg-tertiary mt-1 leading-relaxed flex items-center gap-1.5">
          <span
            className="inline-block w-2.5 h-2.5 rounded-sm bg-positive opacity-70 shrink-0"
            style={HATCH_STYLE}
            aria-hidden="true"
          />
          Hatched bars are estimate (no lot lineage yet).
        </p>
      )}

      <table className="sr-only">
        <caption>Weekly margin flow, last 12 weeks</caption>
        <thead>
          <tr>
            <th>Week</th>
            <th>Net proceeds</th>
            <th>COGS</th>
            <th>Margin</th>
            <th>Units covered</th>
            <th>Grade</th>
          </tr>
        </thead>
        <tbody>
          {weeks.map((w) => (
            <tr key={w.weekStart}>
              <td>{weekLabel(w.weekStart)}</td>
              <td>{money(w.netProceedsCents)}</td>
              <td>{money(w.cogsCents)}</td>
              <td>{signedMoney(w.marginCents)}</td>
              <td>
                {w.cogsCoveredUnits} of {w.totalUnits}
              </td>
              <td>{w.marginGrade}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
